import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AdminLayout } from './AdminDashboard';
import { LuCalendar, LuUsers, LuBed, LuChevronUp, LuChevronDown, LuMessageSquare, LuPhone, LuMail, LuTrash2 } from 'react-icons/lu';

const statusColors = {
  new: { bg: '#e8f4e8', color: '#2d5a2d' },
  replied: { bg: '#e8f0f8', color: '#1a4a8a' },
  booked: { bg: '#fdf6e3', color: '#856404' },
  cancelled: { bg: '#fff0f0', color: '#c00' },
};

export default function AdminInquiries() {
  const [filterStatus, setFilterStatus] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const qc = useQueryClient();

  const { data: inquiries } = useQuery({
    queryKey: ['admin-inquiries-all'],
    queryFn: () => axios.get('/api/inquiries').then(r => r.data).catch(() => [])
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }) => axios.put(`/api/inquiries/${id}`, { status }),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries(['admin-inquiries-all']); }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axios.delete(`/api/inquiries/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['admin-inquiries-all']); }
  });

  const all = inquiries || [];
  const filtered = filterStatus === 'all' ? all : all.filter(i => i.status === filterStatus);

  return (
    <>
      <Helmet><title>Inquiries – Villa Tesori Admin</title></Helmet>
      <AdminLayout title="Booking Inquiries">
        {/* Status tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {['all', 'new', 'replied', 'booked', 'cancelled'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{
              padding: '8px 18px', border: '1px solid', borderRadius: 4,
              borderColor: filterStatus === s ? 'var(--forest)' : '#ddd',
              background: filterStatus === s ? 'var(--forest)' : 'transparent',
              color: filterStatus === s ? '#fff' : 'var(--text-muted)',
              fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', cursor: 'pointer',
              textTransform: 'capitalize'
            }}>
              {s} {s !== 'all' && `(${all.filter(i => i.status === s).length})`}
              {s === 'all' && `(${all.length})`}
            </button>
          ))}
        </div>

        <div style={{ background: 'var(--white)', borderRadius: 8, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          {filtered.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No inquiries found</div>
          )}
          {filtered.map(inq => (
            <div key={inq._id} style={{ borderBottom: '1px solid #f5f5f5' }}>
              <div
                style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setExpanded(expanded === inq._id ? null : inq._id)}
              >
                <div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontWeight: 500 }}>{inq.name}</span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{inq.email}</span>
                    {inq.phone && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{inq.phone}</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <LuCalendar size={14} /> 
                      {inq.checkIn && `${new Date(inq.checkIn).toLocaleDateString('en-IN')} → ${new Date(inq.checkOut).toLocaleDateString('en-IN')}`}
                    </span>
                    {inq.guests && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <LuUsers size={14} /> {inq.guests} guests
                      </span>
                    )}
                    {inq.roomPreference && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <LuBed size={14} /> {inq.roomPreference}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: '0.72rem', ...statusColors[inq.status || 'new'] }}>
                    {inq.status || 'new'}
                  </span>
                  <span style={{ fontSize: '0.78rem', color: '#bbb' }}>
                    {new Date(inq.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'flex' }}>
                    {expanded === inq._id ? <LuChevronUp size={18} /> : <LuChevronDown size={18} />}
                  </span>
                </div>
              </div>

              {expanded === inq._id && (
                <div style={{ padding: '0 24px 20px', background: '#fafafa' }}>
                  <div style={{ background: 'var(--white)', padding: '16px 20px', borderRadius: 6, marginBottom: 16, border: '1px solid #eee' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Message</div>
                    <p style={{ fontSize: '0.9rem', lineHeight: 1.8, color: 'var(--text-body)' }}>{inq.message}</p>
                    {inq.specialRequests && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Special Requests</div>
                        <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>{inq.specialRequests}</p>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginRight: 8, lineHeight: '34px' }}>Update status:</span>
                    {['new', 'replied', 'booked', 'cancelled'].map(s => (
                      <button key={s} onClick={() => updateMutation.mutate({ id: inq._id, status: s })}
                        style={{
                          padding: '7px 16px', borderRadius: 4, border: '1px solid', cursor: 'pointer',
                          fontSize: '0.78rem', textTransform: 'capitalize', fontFamily: 'Jost, sans-serif',
                          background: inq.status === s ? 'var(--forest)' : 'transparent',
                          borderColor: inq.status === s ? 'var(--forest)' : '#ddd',
                          color: inq.status === s ? '#fff' : 'var(--text-muted)',
                        }}>{s}</button>
                    ))}
                    <a href={`mailto:${inq.email}`} className="btn btn-primary" style={{ padding: '7px 16px', fontSize: '0.78rem', marginLeft: 'auto' }}>
                      Reply via Email →
                    </a>
                    {inq.phone && (
                      <a href={`https://wa.me/${inq.phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                        style={{ padding: '7px 16px', background: '#25d366', color: '#fff', borderRadius: 4, fontSize: '0.78rem', display: 'inline-block' }}>
                        WhatsApp
                      </a>
                    )}
                    <button onClick={() => { if (window.confirm('Delete?')) deleteMutation.mutate(inq._id); }}
                      style={{ padding: '7px 14px', background: 'none', border: '1px solid #fdd', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', color: '#c00' }}>
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </AdminLayout>
    </>
  );
}
